"use client";

import { useMemo, Suspense } from "react";

import clsx from "clsx";
import { useParams, useSearchParams, useRouter, usePathname } from "next/navigation";

import { useTranslation } from "~/app/hooks/useTranslation";
import hero1 from "~/assets/images/privacyPolicy/hero1.svg?url";

import SectionWrapper from "../../../common/components/SectionWrapper";

import { CookiePolicyEN, CookiePolicyTR } from "./components/CookiePolicyContent";
import { PrivacyPolicyEN, PrivacyPolicyTR } from "./components/PrivacyPolicyContent";
import { TermsOfUseEN, TermsOfUseTR } from "./components/TermsOfUseContent";
import styles from "./page.module.scss";

function TitleSection() {
  const { t } = useTranslation();
  return (
    <div className={clsx(styles.titleSection, "card")}>
      <h1 className={"pal-section-title"} title={t("privacyPolicy.titleSection.title")}>{t("privacyPolicy.titleSection.title")}</h1>
    </div>
  );
}

function SideSelectionComponent({ options = [], selected, onSelect, locale }) {
  return (
    <div className={styles.sideSelection}>
      <ul>
        {options.map((option, index) => (
          <li
            key={index}
            className={clsx(selected === option.key && styles.active)}
          >
            <button type="button" aria-pressed={selected === option.key} onClick={() => onSelect?.(option.key)}>
              {option[locale]}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function BodySection() {
  const { t } = useTranslation();
  const { locale } = useParams();
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();

  const options = useMemo(
    () => [
      { key: "privacy-policy", en: t("footer.policyLinks.privacyPolicy"), tr: t("footer.policyLinks.privacyPolicy") },
      { key: "terms-of-use", en: t("footer.policyLinks.termsOfUse"), tr: t("footer.policyLinks.termsOfUse") },
      // {
      //   key: "terms-of-service",
      //   en: t("footer.policyLinks.termsOfService"),
      //   tr: t("footer.policyLinks.termsOfService"),
      // },
      { key: "cookie-policy", en: t("footer.policyLinks.cookiePolicy"), tr: t("footer.policyLinks.cookiePolicy") },
    ],
    [t]
  );

  const paramTab = searchParams.get("tab");
  const selectedOption = options.some((o) => o.key === paramTab) ? paramTab : "privacy-policy";

  const handleSelect = (key) => {
    if (key === selectedOption) return;
    const next = new URLSearchParams(searchParams);
    next.set("tab", key);
    router.replace(`${pathname}?${next.toString()}`, { scroll: false });
  };

  const renderContent = () => {
    switch (selectedOption) {
      case "privacy-policy":
        return locale === "tr" ? <PrivacyPolicyTR /> : <PrivacyPolicyEN />;
      case "terms-of-use":
        return locale === "tr" ? <TermsOfUseTR /> : <TermsOfUseEN />;
      // case "terms-of-service":
      //   return <p>{t("privacyPolicy.termsOfServiceContent")}</p>;
      case "cookie-policy":
        return locale === "tr" ? <CookiePolicyTR /> : <CookiePolicyEN />;
      default:
        return locale === "tr" ? <PrivacyPolicyTR /> : <PrivacyPolicyEN />;
    }
  };

  return (
    <div className={clsx(styles.bodySection, "card")}>
      <div className={styles.bodySectionLeft}>
        <SideSelectionComponent options={options} selected={selectedOption} onSelect={handleSelect} locale={locale} />
      </div>
      <div className={styles.bodySectionRight}>
        <h2 className={styles.policyTitle}>{options.find((opt) => opt.key === selectedOption)?.[locale]}</h2>
        <div className={styles.policyContent}>{renderContent()}</div>
      </div>
    </div>
  );
}

export default function PrivacyPolicy() {
  return (
    <main className={styles.container}>
      <SectionWrapper className={styles.section1} background={hero1}>
        <TitleSection />
      </SectionWrapper>
      <SectionWrapper className={styles.section2}>
        {/* Next.js 15: useSearchParams kullanan client component Suspense içine alınmalı */}
        <Suspense fallback={<div className={styles.loading}>Loading...</div>}>
          <BodySection />
        </Suspense>
      </SectionWrapper>
    </main>
  );
}
